import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { Loader2Icon } from 'lucide-react';

const buttonVariants = cva(
  "group/btn relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition duration-200 ease-[cubic-bezier(0.34,1.4,0.5,1)] motion-reduce:transition-none active:scale-[0.96] disabled:pointer-events-none disabled:opacity-50 disabled:active:scale-100 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        // Primary CTA reads as a raised "keycap": mode-aware depth (dark mode
        // = faint white halo to lift off the canvas + dark inset bottom edge;
        // light mode = top highlight + bottom inset + soft drop). Hover lifts
        // 1px and deepens; press settles back down. No opacity fade — the
        // button stays full-contrast. `shadow-[…]` (not a raw box-shadow) so the
        // focus ring still composes.
        default:
          'bg-foreground text-background hover:-translate-y-px active:translate-y-0 shadow-[0_1px_2px_rgba(20,23,43,0.06),inset_0_1px_0_rgba(255,255,255,0.55),inset_0_-2px_3px_-1px_rgba(20,23,43,0.08)] hover:shadow-[0_5px_12px_-3px_rgba(20,23,43,0.14),inset_0_1px_0_rgba(255,255,255,0.65),inset_0_-2px_3px_-1px_rgba(20,23,43,0.1)] dark:shadow-[0_2px_10px_-2px_rgba(255,255,255,0.06),inset_0_-2px_3px_-1px_rgba(0,0,0,0.12)] dark:hover:shadow-[0_5px_18px_-3px_rgba(255,255,255,0.12),inset_0_-2px_3px_-1px_rgba(0,0,0,0.16)]',
        destructive:
          'bg-destructive text-white shadow-elev-1 hover:bg-ruby/30 hover:border hover:border-ruby hover:text-ruby focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
        outline: 'bg-secondary border shadow-premium hover:bg-card/50',
        secondary:
          'bg-secondary border border-border text-foreground shadow-premium hover:bg-button-hovered dark:bg-white/5 dark:border-white/5 dark:hover:bg-white/10',
        ghost:
          'hover:bg-accent hover:shadow-elev-1 dark:hover:bg-white/5 active:scale-100',
        link: 'text-foreground underline-offset-4 hover:underline active:scale-100',
        icon: 'rounded-md padding-[10px] border',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-lg gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-lg px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
      loading: {
        true: 'text-transparent',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant,
  size,
  children,
  disabled,
  asChild = false,
  loading = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  const resolvedVariant = variant ?? 'default';
  // Spotlight is for the calmer variants. Link is text-only (a glow box behind
  // it reads wrong), and the primary CTA has its own keycap depth + sheen — a
  // currentColor spotlight there would be a dark smudge on the white surface.
  const showSpotlight =
    resolvedVariant !== 'link' && resolvedVariant !== 'default';
  // The sheen wipe is reserved for primary CTAs so it stays a special accent.
  const showSheen = resolvedVariant === 'default';

  // Track the cursor inside the button (cheap: writes CSS vars, no re-render)
  // so the spotlight overlay can follow it. Both effects use `currentColor`,
  // which always contrasts the button's own surface, so they read on every
  // variant in both light and dark.
  const handleMouseMove = showSpotlight
    ? (e: React.MouseEvent<HTMLButtonElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        e.currentTarget.style.setProperty('--mx', `${e.clientX - rect.left}px`);
        e.currentTarget.style.setProperty('--my', `${e.clientY - rect.top}px`);
        props.onMouseMove?.(e);
      }
    : props.onMouseMove;

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className, loading }))}
      disabled={disabled || loading}
      {...props}
      onMouseMove={handleMouseMove}
    >
      {loading && (
        <Loader2Icon
          className={cn(
            'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 animate-spin text-muted-foreground'
          )}
        />
      )}
      {showSpotlight && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-300 ease-out group-hover/btn:opacity-100 motion-reduce:hidden"
          style={{
            background:
              'radial-gradient(120px circle at var(--mx, 50%) var(--my, 50%), color-mix(in srgb, currentColor 15%, transparent), transparent 65%)',
          }}
        />
      )}
      {showSheen && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit] motion-reduce:hidden"
        >
          <span
            className="absolute inset-y-0 left-0 w-1/2 opacity-0 group-hover/btn:[animation:btn-sheen_0.5s_ease-out]"
            style={{
              background:
                'linear-gradient(90deg, transparent, color-mix(in srgb, currentColor 22%, transparent), transparent)',
            }}
          />
        </span>
      )}
      <Slottable>{children}</Slottable>
    </Comp>
  );
}

export { Button, buttonVariants };
